// Dictation (the composer's microphone): your words are written out live as you speak (Kareem, 4 Nov), streamed
// through our server to live speech to text (voice/live.ts). About two and a half seconds of quiet after you spoke ends
// it by itself; Stop ends it; Send while dictating writes down the last words and sends, in one go. Where live
// transcription cannot start (no AudioWorklet, the server has no live service), it falls back to recording and
// transcribing the whole thing (lib/recorder.ts), as before.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useMotionValue, type MotionValue } from 'motion/react';
import { LEVEL_START, LEVEL_STOP, MAX_SECONDS, meterLevel, micError, useRecorder, type OnText } from '../lib/recorder';
import { resumeAudioContext } from './audio';
import { canStream, openLive, tapPcm } from './live';

export type DictState = 'idle' | 'asking' | 'recording' | 'working';
/** Quiet after speech that ends a dictation by itself. */
const QUIET_MS = 2500;

export function useDictation(onText: OnText) {
  const cb = useRef(onText);
  cb.current = onText;
  const batch = useRecorder((t, demo, o) => cb.current(t, demo, o));
  const [state, setState] = useState<DictState>('idle');
  const [said, setSaid] = useState('');
  const [error, setError] = useState('');
  const [seconds, setSeconds] = useState(0);
  const [usingBatch, setUsingBatch] = useState(false);
  const level = useMotionValue(0);
  const bars = useRef<number[]>(Array(28).fill(0));
  const s = useRef<{ stream: MediaStream; stopMeter: () => void; stopTap: (() => void) | null; link: ReturnType<typeof openLive>; finals: string[]; partial: string; timer: number; done: boolean } | null>(null);

  const text = () => { const x = s.current; return x ? [...x.finals, x.partial].filter(Boolean).join(' ') : ''; };
  const cleanup = useCallback(() => {
    const x = s.current;
    if (!x) return;
    s.current = null;
    window.clearInterval(x.timer);
    x.stopMeter(); x.stopTap?.();
    x.stream.getTracks().forEach(t => t.stop());
    x.link.close();
    level.set(0);
  }, [level]);
  useEffect(() => cleanup, [cleanup]);

  const finish = useCallback(async (send: boolean) => {
    const x = s.current;
    if (!x || x.done) return;
    x.done = true;
    setState('working');
    await x.link.commit();
    const words = text();
    cleanup();
    setSaid('');
    setState('idle');
    if (words.trim()) cb.current(words.trim(), false, { send });
    else setError("Didn't catch that. Try again.");
  }, [cleanup]);

  const start = useCallback(async () => {
    setError(''); setSaid('');
    // In the tap, before anything is awaited (iOS): the AudioContext the microphone runs through must be running.
    resumeAudioContext();
    if (!canStream()) { setUsingBatch(true); return batch.start(); }
    setUsingBatch(false);
    setState('asking');
    let stream: MediaStream;
    try { stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } }); }
    catch (e) { setState('idle'); setError(micError(e)); return; }
    let spoke = false, loud = 0, quietSince = 0, frame = 0;
    const link = openLive({ mode: 'dictate', turn: 0 }, {
      onPartial: t => { const x = s.current; if (!x) return; x.partial = t; setSaid(text()); },
      onFinal: t => { const x = s.current; if (!x) return; x.finals.push(t); x.partial = ''; setSaid(text()); },
      onError: (m, started) => {
        const x = s.current;
        cleanup(); setSaid('');
        // Live transcription could not start: record the old way instead (the person is already speaking).
        if (!started && x && !x.done) { setState('idle'); setUsingBatch(true); void batch.start(); return; }
        setState('idle'); setError(m);
        const words = x ? [...x.finals, x.partial].filter(Boolean).join(' ') : '';
        if (words) cb.current(words, false, { send: false });
      },
    });
    const stopMeter = meterLevel(stream, (v, live) => {
      level.set(v);
      if (++frame % 3 === 0) { bars.current.push(v); bars.current.shift(); }
      if (!live || !s.current || s.current.done) return;
      const now = performance.now();
      if (!spoke) { loud = v > LEVEL_START ? loud + 1 : 0; if (loud >= 3) spoke = true; return; }
      if (v < LEVEL_STOP) { if (!quietSince) quietSince = now; else if (now - quietSince > QUIET_MS && text()) void finish(false); } else quietSince = 0;
    });
    let n = 0;
    s.current = { stream, stopMeter, stopTap: null, link, finals: [], partial: '', done: false,
      timer: window.setInterval(() => { n += 1; setSeconds(n); if (n >= MAX_SECONDS * 2) void finish(false); }, 1000) };
    setSeconds(0);
    setState('recording');
    try { const stop = await tapPcm(stream, b => link.send(b)); if (s.current) s.current.stopTap = stop; else stop(); }
    catch { cleanup(); setState('idle'); setUsingBatch(true); void batch.start(); }
  }, [batch, cleanup, finish, level]);

  const stop = useCallback((o: { send?: boolean } = {}) => {
    if (usingBatch) { batch.stop(o); return; }
    void finish(!!o.send);
  }, [batch, finish, usingBatch]);

  if (usingBatch) return { state: batch.state as DictState, said: '', error: batch.error, seconds: batch.seconds, level: batch.level as MotionValue<number>, bars: batch.bars, start, stop, live: false };
  return { state, said, error, seconds, level: level as MotionValue<number>, bars, start, stop, live: true };
}
